/* eslint-disable react/destructuring-assignment */
import type { PropsWithChildren } from "react"
import React from "react"
import type { Document, Node } from "@contentful/rich-text-types"
import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import type { Options } from "@contentful/rich-text-react-renderer"
import NextImage from "next/image"

import type { LinkProps, BoxProps } from "@chakra-ui/react"
import {
  Box,
  Text,
  OrderedList,
  UnorderedList,
  ListItem,
  AspectRatio,
  Heading,
  Flex,
} from "@chakra-ui/react"
import { getImageParams } from "lib/cms/utils"
import { Link } from "components/Link"
import { Logger } from "lib/logger"
import { Image } from "components/Image"
import type { Entry } from "contentful"
import { createMarkup } from "utils/createMarkup"
import { getId, checkLinkTarget } from "components/Blog/utils/Blog.utils"
import {
  h1Apercu,
  h2Apercu,
  h3Apercu,
  h4Apercu,
  h5Apercu,
} from "theme/apercuHeadings"
import { fonts } from "theme/themeConfig"
import { HtmlTable } from "./Table"
import { RichTextButton } from "./RichTextButton"
import { TikTokVideo } from "./TikTokVideo"
import { MembershipPerksTooltip } from "./MembershipPerksTooltip"
import { Blockquote } from "./BlogRichTextRenderer/Blockquote"

export type HeaderContentValue = {
  value?: string
}

export type HeaderNode = Node & {
  content?: HeaderContentValue[] // Adding type to ensure spurious tags e.g. bold still allow for cleanTitle function
}

export type BlockquoteLink = {
  uri?: string
}

export type BlockquoteLinkText = {
  value?: string
}

export type ParagraphContents = {
  nodeType: string
  value?: string
  data?: BlockquoteLink
  content?: BlockquoteLinkText[]
}

export type LinkContentProps = LinkProps & {
  onCustomClick?: (href: string) => void
}

const log = Logger("components/Contentful/RichTextRenderer")

interface Props {
  content: Document
  paragraphNode?: React.FC<PropsWithChildren>
  boldNode?: React.FC<PropsWithChildren>
  listItemNode?: React.FC<PropsWithChildren>
  unorderedListNode?: React.FC<PropsWithChildren>
  linkItemNode?: React.FC<PropsWithChildren & { href: string }>
  linkProps?: LinkContentProps
  containerProps?: BoxProps
}

export const RichTextRenderer: React.FC<Props> = ({
  content,
  paragraphNode: ParagraphNode,
  boldNode: BoldNode,
  listItemNode: ListItemNode,
  linkItemNode: LinkItemNode,
  unorderedListNode: UnorderedListNode,
  linkProps,
  containerProps = {},
}) => {
  const options = {
    renderMark: {
      [MARKS.BOLD]: (text: React.ReactNode) =>
        BoldNode ? (
          <BoldNode>{text}</BoldNode>
        ) : (
          <Text as="span" fontWeight="600">
            {text}
          </Text>
        ),
      [MARKS.CODE]: (text: React.ReactNode) => {
        // This function allows for some html tags to be added manually e.g. <sup>, <s>
        if (typeof text === "string" && text.startsWith("<info>")) {
          return <MembershipPerksTooltip info={text} />
        }

        return (
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={createMarkup(text as string)}
          />
        )
      },
    },

    renderNode: {
      [BLOCKS.HEADING_6]: (node: HeaderNode, children: React.ReactNode) => (
        <Heading
          as="h6"
          fontFamily={{ base: "capsM1", md: "caps1" }}
          id={getId(node)}
        >
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_5]: (node: HeaderNode, children: React.ReactNode) => (
        <Heading as="h5" id={getId(node)} {...h5Apercu}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_4]: (node: HeaderNode, children: React.ReactNode) => (
        <Heading as="h4" id={getId(node)} {...h4Apercu}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_3]: (node: HeaderNode, children: React.ReactNode) => (
        <Heading as="h3" id={getId(node)} {...h3Apercu}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_2]: (node: HeaderNode, children: React.ReactNode) => (
        <Heading as="h2" id={getId(node)} {...h2Apercu}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_1]: (node: HeaderNode, children: React.ReactNode) => (
        <Heading as="h1" id={getId(node)} {...h1Apercu}>
          {children}
        </Heading>
      ),
      [BLOCKS.EMBEDDED_ASSET]: (node: Node) => {
        const img = getImageParams(node?.data?.target)
        return (
          img?.src && (
            <Flex
              mt={1}
              mx="auto"
              w="full"
              justifyContent={{ base: "center", md: "start" }}
            >
              <NextImage
                src={img.src}
                alt={img.alt}
                width={img.width}
                height={img.height}
              />
            </Flex>
          )
        )
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
        const { fields, sys } = node?.data?.target ?? {}
        const { id } = sys?.contentType?.sys ?? ""
        if (id === "bynderAsset") {
          const img = getImageParams(fields)
          return (
            img?.src && (
              <Box my={2}>
                <Image {...img} />
              </Box>
            )
          )
        }
        if (id === "video") {
          const { videoLink, title, ratio } = fields ?? ""

          const getRatio = (vidRatio) => {
            switch (vidRatio) {
              case "Square (1:1)":
                return 1
              case "Widescreen (16:9)":
                return 16 / 9
              case "TikTok (9:16)":
                return 9 / 16
              case "Ultrawide (21:9)":
                return 21 / 9
              default:
                return 1
            }
          }
          return (
            <>
              <AspectRatio
                maxW="100%"
                maxH="800px"
                my={2}
                ratio={getRatio(ratio) || 1}
              >
                <iframe
                  width="560"
                  height="315"
                  src={videoLink}
                  title={title}
                  frameBorder="0"
                  allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </AspectRatio>
            </>
          )
        }
        if (id === "htmlTable") {
          const { tableData } = node.data.target.fields.table
          const { colWidths } = node.data.target.fields
          const tableId = node.data.target.sys.id.substring(0, 4)
          if (tableData.length) {
            return (
              <HtmlTable
                tableData={tableData}
                tableId={tableId}
                colWidths={colWidths && colWidths.tableData[0]}
              />
            )
          }
          return null
        }
        if (id === "blogGoogleForm") {
          return (
            <Box
              my="2rem"
              height={{
                base: node.data.target.fields.mobileHeight,
                md:
                  node.data.target.fields.desktopHeight ||
                  node.data.target.fields.mobileHeight,
              }}
            >
              <iframe
                title={node.data.target.fields.title}
                src={node.data.target.fields.iFrameLink}
                width="100%"
                height="100%"
              >
                Loading…
              </iframe>
            </Box>
          )
        }
        if (id === "tikTokVideo") {
          const { videoLink, videoId } = node.data.target.fields
          return <TikTokVideo videoLink={videoLink} videoId={videoId} />
        }
      },
      [BLOCKS.PARAGRAPH]: (_node: Node, children: React.ReactNode) =>
        ParagraphNode ? (
          <ParagraphNode>{children}</ParagraphNode>
        ) : (
          <Text variant="acuminLight" mt="1rem" data-testid="paragraph">
            {children}
          </Text>
        ),
      [BLOCKS.QUOTE]: (node: Node, children: React.ReactNode) => {
        return <Blockquote>{children}</Blockquote>
      },
      [BLOCKS.UL_LIST]: (node: Node, children: React.ReactNode) =>
        UnorderedListNode ? (
          <UnorderedListNode>{children}</UnorderedListNode>
        ) : (
          <UnorderedList>{children}</UnorderedList>
        ),
      [BLOCKS.OL_LIST]: (node: Node, children: React.ReactNode) => {
        return <OrderedList>{children}</OrderedList>
      },
      [BLOCKS.LIST_ITEM]: (node: Node, children: React.ReactNode) =>
        ListItemNode ? (
          <ListItemNode>{children}</ListItemNode>
        ) : (
          <ListItem fontFamily={fonts.acuminLight}>{children}</ListItem>
        ),
      [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => {
        const { onCustomClick = "", ...props } = linkProps ?? {}
        return LinkItemNode ? (
          <LinkItemNode href={node.data?.uri || ""}>{children}</LinkItemNode>
        ) : (
          <Link
            href={node.data?.uri || ""}
            variant="acuminRegular"
            target={checkLinkTarget(node.data.uri)}
            title={children as string}
            textDecor="underline"
            _hover={{
              textDecor: "underline !important",
            }}
            onClick={() => {
              if (onCustomClick) {
                onCustomClick(node?.data?.uri)
              }
            }}
            {...props}
          >
            {children}
          </Link>
        )
      },
      [INLINES.EMBEDDED_ENTRY]: (node: Node) => {
        const entry = node.data.target as Entry<Record<string, unknown>>
        const contentItemId = entry?.sys?.id ?? "<UnknownId>"
        const { sys } = entry.sys.contentType
        const contentTypeId = sys?.id ?? "<UnknownContentType>"

        if (contentTypeId === "cta") {
          return <RichTextButton cta={node.data.target.fields} />
        }

        log.error(
          `Content Type "${contentTypeId}" (${contentItemId}) cannot be embedded inline`
        )
        return null
      },
    },
    // This callback function allows the shift return function to work in the rich text editor
    renderText: (text) => {
      return text.split("\n").reduce((children, textSegment, idx) => {
        const key = `br-${idx}`
        return [...children, idx > 0 && <br key={key} />, textSegment]
      }, [])
    },
  } as unknown as Options
  // This CSS allows for headings to be used next to each other without doubling up spaces
  const headingMarginOverrides = {
    "h1 + h2, h1 + h3, h1 + h4, h1 + h5, h2 + h3, h2 + h4, h3 + h4, h3 + h2, h3 + h5, h4 + h3, h4 + h3, h4 + h5":
      {
        marginTop: "1rem",
      },
  }
  return (
    <Box css={headingMarginOverrides} {...containerProps}>
      {documentToReactComponents(content, options)}
    </Box>
  )
}
